import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import omit from 'lodash/omit';
import { Col, Row } from 'react-bootstrap';
import FormFieldMediumUploadRow from '../../containers/FormFieldMediumUploadRow';
import styles from './styles.scss';

const propTypes = {
  activeSection: PropTypes.string.isRequired,
  appliedMedia: PropTypes.shape({
    primary: PropTypes.shape({
      alt: PropTypes.string,
      title: PropTypes.string,
      mediumId: PropTypes.string,
    }),
  }),
  facility: PropTypes.shape({
    primaryImageMediumId: PropTypes.string,
  }).isRequired,
  onResetDefault: PropTypes.func.isRequired,
  setActiveSection: PropTypes.func.isRequired,
  setAppliedMedia: PropTypes.func.isRequired,
  setShowMediaGallery: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  values: PropTypes.shape({
    primaryImageMediumId: PropTypes.string,
  }).isRequired,
};

const defaultProps = {
  appliedMedia: {},
};

const PrimaryFacilityImageForm = ({
  activeSection,
  appliedMedia,
  setAppliedMedia,
  facility,
  listing,
  onResetDefault,
  setActiveSection,
  setShowMediaGallery,
  setFieldValue,
  values,
  ...rest
}) => {
  const { displayName, address } = facility;

  // this removes the __typename field in the address so it doesn't show in the altText
  const formattedAddress = omit(address, '__typename');
  const defaultAlt = `${displayName} ${Object.values(formattedAddress).join(' ')}`;

  useEffect(() => {
    if (listing && listing.primaryImageMediumId) {
      setFieldValue('primaryImageMediumId', listing.primaryImageMediumId);
    } else {
      setFieldValue('primaryImageMediumId', '');
    }
  }, [listing]);

  useEffect(() => {
    if (appliedMedia && appliedMedia[activeSection]) {
      setFieldValue('primaryImageMediumId', appliedMedia[activeSection].mediumId);
    }
  }, [appliedMedia]);

  const handleReset = () => {
    setFieldValue(
      'primaryImage',
      {
        alt: '',
        title: '',
      },
    );
    setFieldValue('primaryImageMediumId', '');
    if (appliedMedia) {
      setAppliedMedia(activeSection, null);
    }
  };

  return (
    <Row>
      <Col xs={12}>
        <span className={styles.title}>Primary Facility Image</span>
      </Col>
      <Col xs={12}>
        <span>
          This image will display when this facility is listed in widgets across your site.
        </span>
      </Col>
      <Col xs={12} className={styles.primaryImageCol}>
        <FormFieldMediumUploadRow
          activeSection={activeSection}
          defaultAlt={defaultAlt}
          emptyMessage="Once a primary facility image is chosen you will be able to edit its alt and title here."
          mediumId={values.primaryImageMediumId}
          altField="primaryImage.alt"
          mediumField="primaryImageMediumId"
          titleField="primaryImage.title"
          handleReset={handleReset}
          setActiveSection={setActiveSection}
          setAppliedMedia={setAppliedMedia}
          setShowMediaGallery={setShowMediaGallery}
          setFieldValue={setFieldValue}
          values={values}
          {...rest}
        />
      </Col>
    </Row>
  );
};

PrimaryFacilityImageForm.fragments = {
  facility: gql`
    fragment PrimaryImageFormFacility on Facility {
      id
      primaryImageMediumId
      displayName
      address {
        address1
        address2
        city
        state
        postal
      }
    }
  `,
  listing: gql`
    fragment PrimaryImageFormListing on Listing {
      id
      primaryImageMediumId
    }
  `,
};

PrimaryFacilityImageForm.propTypes = propTypes;
PrimaryFacilityImageForm.defaultProps = defaultProps;

export default PrimaryFacilityImageForm;
